import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Loader2, 
  User, 
  Phone, 
  DollarSign,
  FileText,
  AlertCircle
} from 'lucide-react';
import api from '@/lib/api';

interface LoteDisparosTableProps {
  loteId: string;
  metricType: 'total' | 'enviados' | 'falhas' | 'pendentes' | 'invalidos' | 'baixados';
  clientId: number;
  initialStatusFilter?: string; // Filtro inicial de status
}

interface DisparoData {
  id: number;
  lote_id: string;
  nome: string;
  placa: string;
  modelo: string;
  valor: string;
  vencimento: string;
  contato: string;
  status: string;
  data_envio: string;
  nosso_numero: string;
  motivo_rejeicao?: string;
  cliente: string;
  // Entrega real reportada pela Meta (via relay cleoia)
  delivery_status?: 'sent' | 'delivered' | 'read' | 'failed' | null;
  delivery_error_code?: number | null;
  delivery_error_title?: string | null;
  delivery_error_pt?: string | null;
  delivery_error_categoria?: string | null;
  delivery_error_categoria_label?: string | null;
  delivery_updated_at?: string | null;
}

// Entrega real (Meta). "Aceito Meta" = aceito mas sem confirmação de entrega;
// "Entregue"/"Lido" = chegou/abriu; "Falhou" = não entregue (ver motivo).
const DELIVERY_BADGE: Record<string, { label: string; className: string }> = {
  delivered: { label: 'Entregue', className: 'bg-emerald-100 text-emerald-700' },
  read: { label: 'Lido', className: 'bg-sky-100 text-sky-700' },
  sent: { label: 'Aceito Meta', className: 'bg-gray-100 text-gray-600' },
  failed: { label: 'Falhou', className: 'bg-red-100 text-red-700' },
};

// Motivo da falha em PT (com código e categoria) p/ tooltip/legenda.
const deliveryFailTooltip = (d: {
  delivery_error_pt?: string | null;
  delivery_error_title?: string | null;
  delivery_error_code?: number | null;
  delivery_error_categoria_label?: string | null;
}): string => {
  const base = d.delivery_error_pt || d.delivery_error_title || 'Falha na entrega';
  const code = d.delivery_error_code ? ` (cód ${d.delivery_error_code})` : '';
  const cat = d.delivery_error_categoria_label ? ` · ${d.delivery_error_categoria_label}` : '';
  const meta = d.delivery_error_title && d.delivery_error_title !== base ? ` · Meta: ${d.delivery_error_title}` : '';
  return `${base}${code}${cat}${meta}`;
};

interface LoteStats {
  total_disparos: number;
  enviados: number;
  falhas: number;
  pendentes: number;
  valor_total: number;
  cliente: string;
}

const LoteDisparosTable: React.FC<LoteDisparosTableProps> = ({ 
  loteId, 
  metricType, 
  clientId,
  initialStatusFilter
}) => {
  const [loading, setLoading] = useState(true);
  const [disparos, setDisparos] = useState<DisparoData[]>([]);
  const [loteStats, setLoteStats] = useState<LoteStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>(initialStatusFilter || 'todos');

  const fetchDisparos = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 [LoteDisparosTable] Props recebidas:', { loteId, metricType, clientId });
      console.log('🔍 [LoteDisparosTable] Tipo do loteId:', typeof loteId);
      console.log('🔍 [LoteDisparosTable] Valor do loteId:', loteId);
      
      if (!loteId || loteId === 'undefined' || loteId === 'null') {
        throw new Error('Lote ID não fornecido ou inválido');
      }
      
      const url = `/analytics/disparos/detailed/${metricType}/lote/${loteId}?client_id=${clientId}&status=${statusFilter}`;
      console.log('🔗 [LoteDisparosTable] URL completa:', url);
      console.log('🔍 [LoteDisparosTable] Filtro de status:', statusFilter);
      
      const response = await api.get(url);
      console.log('📊 [LoteDisparosTable] Resposta da API:', response);
      
      if (response.success && response.data) {
        setDisparos(response.data.disparos || []);
        setLoteStats(response.data.stats || null);
        console.log('✅ [LoteDisparosTable] Disparos carregados:', response.data.disparos?.length || 0);
        console.log('✅ [LoteDisparosTable] Stats do lote:', response.data.stats);
      } else {
        throw new Error(response.error || 'Erro ao carregar disparos');
      }
    } catch (error: any) {
      console.error('❌ [LoteDisparosTable] Erro ao buscar disparos:', error);
      setError(error.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDisparos();
  }, [loteId, metricType, clientId, statusFilter]);


  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Carregando disparos do lote...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-medium">Erro ao carregar dados</p>
        <p className="text-gray-600 text-sm mt-1">{error}</p>
        <Button 
          onClick={fetchDisparos} 
          variant="outline" 
          size="sm" 
          className="mt-4"
        >
          Tentar Novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Estatísticas do Lote */}
      {loteStats && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Estatísticas do Lote {loteId}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {loteStats.total_disparos}
                </div>
                <div className="text-sm text-gray-600">Total</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {loteStats.enviados}
                </div>
                <div className="text-sm text-gray-600">Enviados</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {loteStats.falhas}
                </div>
                <div className="text-sm text-gray-600">Falhas</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {loteStats.pendentes}
                </div>
                <div className="text-sm text-gray-600">Pendentes</div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Cliente:</span>
                <span className="text-sm text-gray-600">{loteStats.cliente}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-sm font-medium text-gray-700">Valor Total:</span>
                <span className="text-sm font-bold text-green-600">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(loteStats.valor_total)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela de Disparos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Disparos do Lote ({disparos.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {disparos.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Nenhum disparo encontrado para este lote</p>
            </div>
          ) : (
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[250px]">Nome</TableHead>
                    <TableHead className="min-w-[120px]">Placa</TableHead>
                    <TableHead className="min-w-[200px]">Modelo</TableHead>
                    <TableHead className="min-w-[140px]">Valor</TableHead>
                    <TableHead className="min-w-[180px]">Contato</TableHead>
                    <TableHead className="min-w-[130px]">Entrega</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {disparos.map((disparo) => (
                    <TableRow key={disparo.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="truncate">{disparo.nome}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{disparo.placa}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        <span className="truncate block">{disparo.modelo}</span>
                      </TableCell>
                      <TableCell className="font-medium text-green-600">
                        {disparo.valor}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-gray-500 flex-shrink-0" />
                          <span className="text-sm truncate">{disparo.contato}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {disparo.delivery_status ? (
                          <div className="flex flex-col gap-0.5">
                            <Badge
                              className={(DELIVERY_BADGE[disparo.delivery_status] || DELIVERY_BADGE.sent).className}
                              title={
                                disparo.delivery_status === 'failed'
                                  ? deliveryFailTooltip(disparo)
                                  : disparo.delivery_updated_at || ''
                              }
                            >
                              {(DELIVERY_BADGE[disparo.delivery_status] || { label: disparo.delivery_status }).label}
                            </Badge>
                            {disparo.delivery_status === 'failed' && (disparo.delivery_error_pt || disparo.delivery_error_title) && (
                              <span className="text-[11px] leading-tight text-red-600">
                                {disparo.delivery_error_pt || disparo.delivery_error_title}
                                {disparo.delivery_error_categoria_label ? ` · ${disparo.delivery_error_categoria_label}` : ''}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoteDisparosTable;
